import type {
  AnalystForecastApiData,
  CorporateEventApiItem,
  FundamentalsApiItem,
  ProgressTermApiData,
  ScoreApiData,
  StockReportPdfLinkApiData,
  SummaryApiResponse,
} from '@app-types/summaryApi';
import type { ProgressMetricSelection } from '@constants/progressMetrics';
import type { InstrumentQueryParams } from '@utils/queryParams';
import { summaryClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

/** Summary-flavoured alias of the shared envelope-error helper. */
const throwSummaryApiError = (
  body: SummaryApiResponse<unknown> | undefined,
  status: number | undefined,
  fallbackMessage: string,
): never => throwDataApiError(body, status, fallbackMessage);

/**
 * GET /api/summary/score?ticker=&mkt=
 * Sends the auth token via the `x-access-token` header (summaryClient interceptor).
 */
export const fetchSummaryScore = async (
  params: InstrumentQueryParams,
): Promise<ScoreApiData> => {
  try {
    const response = await summaryClient.get<SummaryApiResponse<ScoreApiData>>('/score', {
      params: { ticker: params.ticker, mkt: params.mkt },
    });

    if (response.data.status !== 0 || !response.data.data) {
      throwSummaryApiError(
        response.data,
        response.status,
        'Summary score response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/summary/stocks/pdf-link?ticker=&mkt=
 * Returns a short-lived StockReports+ PDF URL opened from the Enterprise Score link.
 */
export const fetchSummaryStockReportPdfLink = async (
  params: InstrumentQueryParams,
): Promise<string> => {
  try {
    const response = await summaryClient.get<SummaryApiResponse<StockReportPdfLinkApiData>>(
      '/stocks/pdf-link',
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    if (response.data.status !== 0) {
      throwSummaryApiError(
        response.data,
        response.status,
        'Stock report PDF link response is invalid.',
      );
    }

    const pdfLink = response.data.data?.pdfLink?.trim();
    if (pdfLink) return pdfLink;

    return throwSummaryApiError(
      response.data,
      response.status,
      'Stock report PDF link response is invalid.',
    );
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/summary/fundamentals?ticker=&mkt=
 * Returns investment indicator values (pbr, per, roe, roa).
 */
export const fetchSummaryFundamentals = async (
  params: InstrumentQueryParams,
): Promise<FundamentalsApiItem[]> => {
  try {
    const response = await summaryClient.get<SummaryApiResponse<FundamentalsApiItem[]>>(
      '/fundamentals',
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwSummaryApiError(
        response.data,
        response.status,
        'Summary fundamentals response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/summary/corporate-event?ticker=&mkt=
 * Returns schedule dates (earnings, lastRight, exRight, shareholders).
 */
export const fetchSummaryCorporateEvents = async (
  params: InstrumentQueryParams,
): Promise<CorporateEventApiItem[]> => {
  try {
    const response = await summaryClient.get<SummaryApiResponse<CorporateEventApiItem[]>>(
      '/corporate-event',
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwSummaryApiError(
        response.data,
        response.status,
        'Summary corporate event response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/summary/stocks/analyst-forecast?ticker=&mkt=
 * Returns the section update timestamp, target price, consensus breakdown,
 * and Enterprise Score outlook data.
 */
export const fetchSummaryAnalystForecast = async (
  params: InstrumentQueryParams,
): Promise<AnalystForecastApiData> => {
  try {
    const response = await summaryClient.get<SummaryApiResponse<AnalystForecastApiData>>(
      '/stocks/analyst-forecast',
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    const body = response.data;
    if (body.status !== 0 || !body.data || !Array.isArray(body.data.breakdown)) {
      throwSummaryApiError(
        body,
        response.status,
        'Summary analyst forecast response is invalid.',
      );
    }

    return body.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

export interface SummaryProgressTermRequest extends InstrumentQueryParams {
  metric: ProgressMetricSelection['metric'];
}

/**
 * GET /api/summary/progress/term?ticker=&mkt=&metric=
 * Returns quarterly actuals + forecasts for the selected progress metric.
 * `metric` is whitelisted server-side
 * (revenue/operatingProfit/recurringProfit/netIncome, default recurringProfit);
 * the RKD column mapping lives on the server, not here.
 */
export const fetchSummaryProgressTerm = async (
  params: SummaryProgressTermRequest,
): Promise<ProgressTermApiData> => {
  try {
    const response = await summaryClient.get<SummaryApiResponse<ProgressTermApiData>>(
      '/progress/term',
      {
        params: {
          ticker: params.ticker,
          mkt: params.mkt,
          metric: params.metric,
        },
      },
    );

    if (response.data.status !== 0 || !response.data.data) {
      throwSummaryApiError(
        response.data,
        response.status,
        'Summary progress term response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};
